// [CONTROLS] — a small free-form condition/action language (`RULE name`
// then IF/AND/OR/THEN/ELSE/PRIORITY clause lines) rather than a tabular
// section — so unlike every other editor here, a rule's own body is kept as
// opaque multi-line TEXT, not parsed into typed fields.
//
// `VARIABLE`/`EXPRESSION` declarations are GLOBAL to the whole section, not
// part of any one rule's own body — kept entirely separate
// (`readControlVariables`/`readControlExpressions`), never absorbed into a
// `ControlRule.body` even when interspersed between RULE blocks, a real
// legal pattern.

import { nextAvailableName } from "./nextAvailableName.ts";
import { writeOrDeleteSection } from "./writeOrDeleteSection.ts";

export interface ControlRule {
  name: string;
  /** The RULE name line itself isn't included here. */
  body: string;
}

/** A named alias for an object/attribute reference, usable as either side of ANY rule's own premise. Global to the whole section — can appear anywhere among the RULE blocks, not just before them. */
interface ControlVariableDef {
  name: string;
  tokens: string[];
}

/** A named math expression referencing `VARIABLE` names, usable as a premise's own LHS in place of a plain object/attribute triple. */
interface ControlExpressionDef {
  name: string;
  expr: string;
}

function isVariableOrExpressionRow(row: string[]): boolean {
  const kw = row[0]?.toUpperCase();
  return kw === "VARIABLE" || kw === "EXPRESSION";
}

export function readControlRules(raw: ReadonlyMap<string, string[][]>): ControlRule[] {
  const rows = raw.get("CONTROLS") ?? [];
  const rules: ControlRule[] = [];
  let current: ControlRule | null = null;

  for (const row of rows) {
    if (row[0]?.toUpperCase() === "RULE" && row[1]) {
      if (current) rules.push(current);
      current = { name: row[1], body: "" };
      continue;
    }
    // Skipping these here keeps a rule's own body text from being corrupted
    // by an interspersed VARIABLE/EXPRESSION declaration line — real, legal
    // even between RULE blocks.
    if (isVariableOrExpressionRow(row)) continue;
    if (!current || row.length === 0) continue;
    const line = row.join(" ");
    current.body = current.body ? `${current.body}\n${line}` : line;
  }
  if (current) rules.push(current);
  return rules;
}

export function readControlVariables(raw: ReadonlyMap<string, string[][]>): ControlVariableDef[] {
  const defs: ControlVariableDef[] = [];
  for (const row of raw.get("CONTROLS") ?? []) {
    if (row[0]?.toUpperCase() !== "VARIABLE" || !row[1] || row[2] !== "=") continue;
    defs.push({ name: row[1], tokens: row.slice(3) });
  }
  return defs;
}

export function readControlExpressions(
  raw: ReadonlyMap<string, string[][]>
): ControlExpressionDef[] {
  const defs: ControlExpressionDef[] = [];
  for (const row of raw.get("CONTROLS") ?? []) {
    if (row[0]?.toUpperCase() !== "EXPRESSION" || !row[1] || row[2] !== "=") continue;
    defs.push({ name: row[1], expr: row.slice(3).join(" ") });
  }
  return defs;
}

export function writeControlRules(
  raw: ReadonlyMap<string, string[][]>,
  rules: ControlRule[]
): Map<string, string[][]> {
  const rows: string[][] = [];
  // Must preserve the ORIGINAL VARIABLE/EXPRESSION declaration rows
  // verbatim — this editor never edits these global declarations, so
  // regenerating the section from `rules` alone would silently drop them.
  // Placed at the top: a declaration must appear BEFORE any rule that
  // references it.
  for (const row of raw.get("CONTROLS") ?? []) {
    if (isVariableOrExpressionRow(row)) rows.push(row);
  }
  for (const rule of rules) {
    rows.push(["RULE", rule.name]);
    for (const line of rule.body.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed) continue;
      rows.push(trimmed.split(/\s+/));
    }
  }
  return writeOrDeleteSection(raw, "CONTROLS", rows);
}

export function nextAvailableControlRuleName(existingNames: Set<string>): string {
  return nextAvailableName(existingNames, "RULE");
}
